import isNil from "lodash-es/isNil";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { WretchError } from "wretch";

import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { DeliveryWindowData } from "../../common/types/deliveryTypes";
import { translateWeekday } from "../../common/utils";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsLoading
} from "../../common/utils/asyncDataUtils";
import { englishDayToDay } from "../../common/utils/dateUtils";
import authStore from "../auth/authStore";

type DeliveryWindow = {
   time: string;
   weekday: string;
};

type DeliveryWindowResponse = {
   customerNumber: string;
   deliveries: Array<{
      day: string;
      deliveryWindows: Array<{
         start: string;
         stop: string;
      }>;
   }>;
};

type DeliveryWindowStoreType = BaseStoreType & {
   dataOwner: string | null;
   init: () => void;
   windows: AsyncData<DeliveryWindow[]>;
   windowsTableData: AsyncData<DeliveryWindowData[]>;
   fetchWindows: () => Promise<void>;
};

const deliveryWindowStore: DeliveryWindowStoreType = makeAutoObservable({
   dataOwner: null,
   windows: initializeWithDefaultData([]),
   windowsTableData: initializeWithDefaultData([]),

   init: () => {
      reaction(
         () => authStore.isLoggedIn(),
         (isLoggedIn) => {
            const { currentCompany } = authStore;
            const { dataOwner } = deliveryWindowStore;
            if (isLoggedIn) {
               if (currentCompany !== dataOwner) {
                  deliveryWindowStore.clearCompanySpecificData();
                  deliveryWindowStore.fetchWindows();
               }
            } else {
               deliveryWindowStore.clearCompanySpecificData();
            }
         }
      );
   },
   fetchWindows: async () => {
      logger.log(
         "delivery",
         "fetchWindows auth state... isLoggedIn: ",
         authStore.isLoggedIn(),
         "; currentCompany: ",
         authStore.currentCompany
      );
      if (!authStore.isLoggedIn() || isNil(authStore.currentCompany)) {
         logger.warn("delivery", "Fetch delivery windows called without being logged in or without a current company");
         return;
      }

      const transitionedSuccessfully = setAsLoading(deliveryWindowStore.windows);
      if (!transitionedSuccessfully) {
         logger.log("delivery", "Already fetching delivery windows... aborting!");
         return;
      }
      setAsLoading(deliveryWindowStore.windowsTableData);

      const { currentCompany } = authStore;
      const url = resolvePlatformRequestUrl(platformRoutes.deliveryWindow, {
         customerNumber: currentCompany
      });
      runInAction(() => {
         deliveryWindowStore.dataOwner = currentCompany;
      });
      apiClient(url, authStore.getSessionToken())
         .get()
         .notFound(() => {
            logger.log("delivery", "No delivery windows found for customer");
            setAsDataAvailable(deliveryWindowStore.windows, []);
            setAsDataAvailable(deliveryWindowStore.windowsTableData, []);
         })
         .json((resp: DeliveryWindowResponse) => {
            const storeData = resp.deliveries.map((delivery) => ({
               time: delivery.deliveryWindows.map((window) => `${window.start} - ${window.stop}`).join(", "),
               weekday: translateWeekday(delivery.day)
            }));

            const windowsData = resp.deliveries
               .map((delivery) => ({
                  day: englishDayToDay(delivery.day),
                  periods: delivery.deliveryWindows
               }))
               .filter((windowsData) => typeof windowsData.day === "number");

            setAsDataAvailable(deliveryWindowStore.windows, storeData);
            setAsDataAvailable(deliveryWindowStore.windowsTableData, windowsData);
         })
         .catch((err: WretchError) => {
            logger.warn("delivery", "Failed to fetch delivery windows", err);
            setAsErrorOccured(deliveryWindowStore.windows, `${err}`);
            setAsErrorOccured(deliveryWindowStore.windowsTableData, `${err}`);
         });
   },
   clearCompanySpecificData: () => {
      deliveryWindowStore.dataOwner = null;
      deliveryWindowStore.windows = initializeWithDefaultData([]);
      deliveryWindowStore.windowsTableData = initializeWithDefaultData([]);
   }
} satisfies DeliveryWindowStoreType);

reaction(
   () => deliveryWindowStore.windowsTableData.progress,
   (progress) => {
      logger.log("delivery", "Delivery windows table data progress changed to ", progress);
   }
);

export default deliveryWindowStore;
